import type { MouseEventHandler } from 'react'
import { useState } from 'react'

import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { Tooltip } from '@b-stock/fe-core/bstock-react'

import { TooltipLabel } from '../ui'
import { FormButton, FormLinkButton } from './ui'

/**
 * One state of the action form, as a single control.
 *
 * Bid Now, Raise Bid, Buy Now, both Sign in states and View Details are the
 * same button in six states — they collapse together and they answer the
 * tooltip question the same way, so they are one component rather than six
 * places to remember the rule.
 *
 * **The tooltip appears only when the label does not.** A control whose label is
 * already on screen gets no tooltip: it would repeat what the buyer can read and
 * add a second, slower way to learn nothing new. The published prototype puts
 * tooltips on the expanded CTAs as well; that is a deliberate departure.
 */
export const ActionFormButton = ({
  description,
  expanded,
  form,
  href,
  icon,
  iconOnly,
  label,
  onClick,
  type = 'submit',
}: {
  /** One line under the label in the tooltip. Never rendered on its own. */
  description: string
  /**
   * Set only on a control that opens an overlay, where it becomes
   * `aria-expanded`. While it is open the tooltip is suppressed: the overlay
   * says what the control did, and the design has tooltips dismiss on any click
   * that opens one.
   */
  expanded?: boolean
  /**
   * The `<form>` to submit, for a button rendered outside it in the DOM. The
   * inline bid overlay portals out of its AG Grid cell and so needs it; a button
   * sitting inside its form does not.
   */
  form?: string
  /** Present for View Details, which navigates rather than submitting. */
  href?: string
  /** Shown in place of the label once the form collapses. */
  icon: IconDefinition
  /** Collapses the button to its icon and gives it a tooltip. */
  iconOnly: boolean
  /** The visible text, and the accessible name when only the icon shows. */
  label: string
  onClick: MouseEventHandler<HTMLElement>
  /** `button` for a control that opens the overlay rather than submitting. */
  type?: 'button' | 'submit'
}) => {
  // The tooltip positions against the control, so it needs the element itself.
  const [element, setElement] = useState<HTMLElement | null>(null)

  const content = iconOnly ? <FontAwesomeIcon icon={icon} /> : label

  return (
    <>
      {href === undefined ? (
        <FormButton
          $iconOnly={iconOnly}
          appearance="primary"
          aria-expanded={expanded}
          aria-label={iconOnly ? label : undefined}
          form={form}
          onClick={onClick}
          ref={setElement}
          size="small"
          type={type}
        >
          {content}
        </FormButton>
      ) : (
        <FormLinkButton
          $iconOnly={iconOnly}
          appearance="primary"
          aria-label={iconOnly ? label : undefined}
          href={href}
          onClick={onClick}
          ref={setElement}
          size="small"
          target="_blank"
        >
          {content}
        </FormLinkButton>
      )}

      {iconOnly && !expanded && (
        <Tooltip referenceElement={element}>
          <TooltipLabel>{label}</TooltipLabel>
          {description}
        </Tooltip>
      )}
    </>
  )
}
